import { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import {
    Trophy, Ticket, Lock, ArrowRight, Gift, TrendingUp,
    Calendar, Clock, Zap, Star, Flame, Spade,
    Dice5, Diamond, Heart, Club, Coins,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import RecentWinsTicker from '../components/RecentWinsTicker';
import DashboardTiles from '../components/DashboardTiles';
import WalletStack from '../components/WalletStack';
import BigWinLeaderboard from '../components/BigWinLeaderboard';
import JackpotsShowcase from '../components/JackpotsShowcase';  // eslint-disable-line no-unused-vars -- jackpots removed from UI (Jun 2026)
import LobbyMusic from '../components/LobbyMusic';
import { useFeatures } from '../hooks/useFeatures';
import PotFundingTicker from '../components/PotFundingTicker';  // eslint-disable-line no-unused-vars -- community pot ticker removed from UI (Jun 2026)
import BoostRibbon from '../components/BoostRibbon';
import LobbyHelpCoach from '../components/LobbyHelpCoach';
import DailySpecialBanner from '../components/DailySpecialBanner';
import LuckyPackBanner from '../components/LuckyPackBanner';
import WheelWidget from '../components/WheelWidget';
// WinnersWall removed 2026-02-26 — declutter directive: keep the player
// dashboard focused on the current wheel + game tiles. Winners are still
// visible via the wheel widget's own recent-winner chip during a fire.
import SeriesPackBanner from '../components/SeriesPackBanner';
import FreePlayProgressBadge from '../components/FreePlayProgressBadge';
import BuyCreditsHomeCard from '../components/BuyCreditsHomeCard';  // eslint-disable-line no-unused-vars -- replaced by WalletStack (Jun 2026), kept for quick restore
import MatchReadyChip from '../components/MatchReadyChip';
import ReceiptCountdownBanner from '../components/ReceiptCountdownBanner';
import LoadBonusPromo from '../components/LoadBonusPromo';
import BonusUpdateBanner from '../components/BonusUpdateBanner';
import RebateRecapModal from '../components/RebateRecapModal';

import NightOwlBanner from '../components/NightOwlBanner';
import { DailySuperBanner } from '../components/DailySuperBanner';
import PokerRecentWinsTicker from '../components/PokerRecentWinsTicker';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATE_NAMES = { TN: 'Tennessee', IL: 'Illinois', MS: 'Mississippi' };
const STATE_COLORS = { TN: '#F59E0B', IL: '#EF4444', MS: '#3B82F6' };
const STATES = ['TN', 'IL', 'MS'];

const fmt$ = (n) => `$${Number(n || 0).toFixed(2)}`;

// "America/Chicago" wall-clock time as a {H, M} object — used for cutoff countdown.
const nowCT = () => {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: 'America/Chicago',
            hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: false,
        }).formatToParts(new Date()).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
        return {
            h: parseInt(parts.hour, 10) % 24,
            m: parseInt(parts.minute, 10),
            s: parseInt(parts.second, 10),
        };
    } catch {
        const d = new Date();
        return { h: d.getHours(), m: d.getMinutes(), s: d.getSeconds() };
    }
};

const parseCutoff = (cutoffStr) => {
    // "1:00 PM" → {h:13, m:0}
    if (!cutoffStr) return null;
    const m = cutoffStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return null;
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ampm = m[3].toUpperCase();
    if (ampm === 'PM' && h !== 12) h += 12;
    if (ampm === 'AM' && h === 12) h = 0;
    return { h, m: min };
};

const minutesUntil = (target) => {
    const t = nowCT();
    const tot = (t.h * 60) + t.m + (t.s / 60);
    const targetMins = (target.h * 60) + target.m;
    let diff = targetMins - tot;
    if (diff < 0) diff += 24 * 60;
    return diff;
};

const formatCountdown = (mins) => {
    if (mins <= 0) return 'closed';
    const h = Math.floor(mins / 60);
    const m = Math.floor(mins % 60);
    if (h >= 1) return `${h}h ${m}m`;
    if (m >= 1) return `${m}m`;
    return '< 1m';
};

const Lobby = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const features = useFeatures();
    const [authMe, setAuthMe] = useState(null);
    const [draws, setDraws] = useState([]);
    const [cutoffs, setCutoffs] = useState({});
    const [liveGames, setLiveGames] = useState([]);
    const [tick, setTick] = useState(0);
    // Per-game availability toggle (admin-controlled, /api/games/availability)
    const [gameAvail, setGameAvail] = useState({ blackjack: true, three_card_poker: true, craps: true, roulette: true, lucky_ducky: true, lucky_flamingo: true, outlaws_loot: true, btpb: true });

    // Fetch which house table games are currently open. Failure → all open
    // (matches DEFAULTS in the API). Polls every 30 s so an admin closing a
    // table is reflected in the lobby within half a minute.
    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/games/availability`);
                if (alive && data) setGameAvail((prev) => ({ ...prev, ...data }));
            } catch { /* keep last known / defaults */ }
        };
        load();
        const id = setInterval(load, 30_000);
        return () => { alive = false; clearInterval(id); };
    }, []);

    // Refresh authMe every 20s for fresh balance / daily bonus / rollover progress
    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/auth/me`);
                setAuthMe(data);
            } catch { /* ignore */ }
        };
        load();
        const id = setInterval(load, 20_000);
        // Also refresh whenever the tab regains focus so wagers in another tab show up fast
        const onVisible = () => { if (document.visibilityState === 'visible') load(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
    }, []);

    // Birthday bonus celebration — fires once when player lands on lobby on their birthday
    useEffect(() => {
        let stash = null;
        try { stash = sessionStorage.getItem('birthdayBonus'); } catch { /* ignore */ }
        if (!stash) return;
        try {
            const { amount } = JSON.parse(stash);
            sessionStorage.removeItem('birthdayBonus');
            // Slight delay so the page has settled
            setTimeout(() => {
                toast.success(`🎂 Happy birthday! +$${(amount || 10).toFixed(2)} bonus credits added!`, {
                    duration: 6000,
                });
                // Bigger 3-burst confetti tailored to the moment
                const colors = ['#FF007F', '#FFD700', '#39FF14', '#00FFFF', '#FFFFFF'];
                const opts = { startVelocity: 55, spread: 90, ticks: 110, zIndex: 9999, colors, scalar: 1.0 };
                confetti({ ...opts, particleCount: 120, angle: 90, origin: { x: 0.5, y: 1.05 } });
                setTimeout(() => {
                    confetti({ ...opts, particleCount: 70, angle: 60, origin: { x: 0, y: 1 } });
                    confetti({ ...opts, particleCount: 70, angle: 120, origin: { x: 1, y: 1 } });
                }, 220);
            }, 600);
        } catch { /* ignore */ }
    }, []);

    // Latest lottery draws (all states, both windows)
    useEffect(() => {
        const load = async () => {
            try {
                // Pull last 2 days × 3 states × 2 picks
                const all = [];
                for (const st of STATES) {
                    const { data } = await axios.get(`${API}/lottery/draws?state=${st}&limit=4`);
                    if (Array.isArray(data)) all.push(...data);
                }
                setDraws(all);
            } catch { /* ignore */ }
        };
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, []);

    // Lottery cutoffs (admin-editable)
    useEffect(() => {
        axios.get(`${API}/lottery/settings`).then(({ data }) => {
            setCutoffs(data?.cutoffs || {});
        }).catch(() => {});
    }, []);

    // Top live / upcoming sports — pulls a popular sport (NFL or top key)
    useEffect(() => {
        const load = async () => {
            try {
                // Try top sports first
                const sportsResp = await axios.get(`${API}/sports`);
                const list = sportsResp.data || [];
                const featured = list.find(s => /football|basketball|baseball/i.test(s.title || '')) || list[0];
                if (!featured) return;
                const { data } = await axios.get(`${API}/sports/${featured.key}/odds`);
                setLiveGames(Array.isArray(data) ? data.slice(0, 6) : []);
            } catch { /* ignore */ }
        };
        load();
    }, []);

    // 1-second tick for countdown updates
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 30_000);
        return () => clearInterval(id);
    }, []);

    // Build per-state next-draw info for lottery tile
    const nextLotteryDraw = useMemo(() => {
        // Find the soonest cutoff among all 6 windows
        const candidates = [];
        for (const st of STATES) {
            ['midday', 'evening'].forEach(window => {
                const cutoff = parseCutoff(cutoffs[`${st}_${window}`]);
                if (!cutoff) return;
                candidates.push({
                    state: st,
                    window,
                    minsAway: minutesUntil(cutoff),
                    cutoffStr: cutoffs[`${st}_${window}`],
                });
            });
        }
        if (candidates.length === 0) return null;
        candidates.sort((a, b) => a.minsAway - b.minsAway);
        return candidates[0];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cutoffs, tick]);

    // Group recent draws by state+window for the today's-lottery grid
    const drawsByKey = useMemo(() => {
        const map = {};
        for (const d of draws) {
            const k = `${d.state}_${d.drawing_time}_${d.pick_type}`;
            if (!map[k] || (map[k].draw_date < d.draw_date)) map[k] = d;
        }
        return map;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draws]);

    const balance = authMe?.credits ?? user?.credits ?? 0;
    const pendingRollover = Number(authMe?.pending_rollover || 0);
    const pendingRolloverTotal = Number(authMe?.pending_rollover_total || 0);
    const dailyBonusReady = false; // Daily bonus retired (Jun 2026) — replaced by the load-to-get bonus

    // Celebrate the moment rollover clears (transitions from >0 to 0)
    const prevRollover = useRef(null);
    useEffect(() => {
        // Skip first render — only fire on real transition
        if (prevRollover.current !== null && prevRollover.current > 0 && pendingRollover === 0) {
            // Triple-burst gold/green confetti
            const colors = ['#FFD700', '#39FF14', '#FF8C00', '#FFFFFF'];
            confetti({
                particleCount: 80, spread: 70, startVelocity: 45,
                origin: { y: 0.55 }, colors, ticks: 200,
            });
            setTimeout(() => confetti({
                particleCount: 60, spread: 90, startVelocity: 35,
                origin: { x: 0.2, y: 0.5 }, colors, ticks: 180,
            }), 180);
            setTimeout(() => confetti({
                particleCount: 60, spread: 90, startVelocity: 35,
                origin: { x: 0.8, y: 0.5 }, colors, ticks: 180,
            }), 360);
            toast.success('🎉 Rollover cleared! Cashout unlocked.', {
                duration: 5000,
                style: {
                    background: '#0a0a0a',
                    border: '1px solid rgba(57,255,20,0.4)',
                    color: '#39FF14',
                },
            });
        }
        prevRollover.current = pendingRollover;
    }, [pendingRollover]);

    return (
        <div className="a11y-font min-h-screen bg-[#080808] relative overflow-x-hidden">
            {/* Ambient animated background — toned down for accessibility */}
            <div className="pointer-events-none fixed inset-0 -z-0 opacity-25">
                <div
                    className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full blur-3xl"
                    style={{
                        background: 'radial-gradient(circle, rgba(255,215,0,0.10) 0%, transparent 70%)',
                    }}
                />
                <div
                    className="absolute top-1/3 -right-40 w-[700px] h-[700px] rounded-full blur-3xl"
                    style={{
                        background: 'radial-gradient(circle, rgba(255,0,127,0.08) 0%, transparent 70%)',
                    }}
                />
            </div>

            <Header />

            {/* Floating "🎁 Match ready — load $10" chip for new players who
                haven't crossed the $10 threshold yet. Auto-hides on claim. */}
            <MatchReadyChip />

            {/* Monday rebate recap — one-time celebratory pop when a rebate landed this week */}
            <RebateRecapModal />

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-8 sm:space-y-10 relative z-10">
                <DailySuperBanner />

                {/* -0.5 After-hours support notice — auto-appears 10 PM – 8 AM CT */}
                <NightOwlBanner />
                {/* -0.25 Live poker wins ticker — social proof */}
                <PokerRecentWinsTicker />
                {/* Recent big wins ticker removed per operator (Jun 2026) — players
                    got upset seeing other players' wins. Component kept for reference. */}
                {false && <RecentWinsTicker />}

                {/* 0.5 Referral bonus boost ribbon (only renders while a boost is active) */}
                <BoostRibbon />

                {/* 0.7 Friendly "how it works" coach card for new/older players (self-dismissing) */}
                <LobbyHelpCoach />

                {/* 0.8 Daily Special — admin-curated featured bet (only renders when one is live) */}
                <DailySpecialBanner />
                {/* 0.83 Series Pack — playoff series prediction (only renders when active) */}
                <SeriesPackBanner />
                {/* 0.85 Lucky 5 — tiered sports lottery (only renders when a pack is active) */}
                <LuckyPackBanner />
                {/* 0.87 Wheel the Names — live promo wheel (only renders when an active/settled wheel exists for this operator) */}
                <WheelWidget />
                {/* Winners Wall removed 2026-02-26 — declutter directive */}
                {/* 1. Bonus-change announcement — daily bonus retired, new load bonus (dismissible) */}
                <BonusUpdateBanner onCtaClick={() => navigate('/buy-credits')} />

                {/* 1. Official wallet stack — Credits / Free Play / Add Credits (Jun 2026) */}
                <WalletStack
                    name={user?.name || user?.email?.split('@')[0] || 'Player'}
                    credits={balance}
                    freePlay={Number(authMe?.bonus_credits || 0)}
                    dailyBonusReady={dailyBonusReady}
                    rollover={pendingRollover}
                />

                {/* 1.1 Free-play wager progress — auto-hides when no rollover */}
                <FreePlayProgressBadge user={authMe} />

                {/* 1.15 Receipt status banner — pending countdown / approved / rejected (auto-hides) */}
                <ReceiptCountdownBanner />

                {/* 1.16 Load-to-get bonus strip — drives repeat deposits (auto-hides if promo off) */}
                <LoadBonusPromo compact onCtaClick={() => navigate('/buy-credits')} />


                {/* Jackpots showcase (Beto Bucks / Community / Daily Super) removed per owner — Jun 2026 */}

                {/* 2. Official Betohalics dashboard tiles — visual handoff (Jun 2026). Each tile opens its existing route. */}
                <DashboardTiles features={features} gameAvail={gameAvail} />

                {/* ───── BELOW THE FOLD — Progressive disclosure for older players ───── */}

                <details className="a11y-disclosure" data-testid="lobby-show-more-wins">
                    <summary>Big Wins This Week</summary>
                    <div className="body">
                        <BigWinLeaderboard />
                    </div>
                </details>

                {/* 3. Today's lottery quick-glance */}
                <details className="a11y-disclosure" data-testid="lobby-show-more-lottery">
                    <summary>Today&apos;s Lottery</summary>
                    <div className="body space-y-3">
                        <div className="flex justify-end">
                            <button
                                onClick={() => navigate('/lottery')}
                                className="text-sm text-[#FFD700] font-semibold hover:underline a11y-tap"
                                data-testid="section-link-todays-lottery-link"
                            >
                                View all →
                            </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                            {STATES.map(st => (
                                <LotteryStateCard
                                    key={st}
                                    state={st}
                                    drawsByKey={drawsByKey}
                                    cutoffs={cutoffs}
                                    onClick={() => navigate('/lottery')}
                                />
                            ))}
                        </div>
                    </div>
                </details>

                {/* 4. Live now sports strip */}
                {liveGames.length > 0 && (
                    <details className="a11y-disclosure" data-testid="lobby-show-more-live">
                        <summary>Live Now · {liveGames.length}</summary>
                        <div className="body space-y-3">
                            <div className="flex justify-end">
                                <button
                                    onClick={() => navigate('/sportsbook')}
                                    className="text-sm text-[#39FF14] font-semibold hover:underline a11y-tap"
                                    data-testid="section-link-live-now-link"
                                >
                                    View all →
                                </button>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                                {liveGames.slice(0, 6).map((g, idx) => (
                                    <LiveGameCard key={g.id || idx} game={g}
                                        onClick={() => navigate('/sportsbook')} />
                                ))}
                            </div>
                        </div>
                    </details>
                )}

                {/* 5. Promotions strip */}
                <details className="a11y-disclosure" open data-testid="lobby-show-more-promotions">
                    <summary>Promotions</summary>
                    <div className="body grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                        <PromoCard
                            color="#39FF14"
                            icon={<Star className="w-5 h-5" />}
                            title="Load & Get Free Play"
                            subtitle="Load $20+ to earn Free Play — the more you load, the bigger the bonus. Every 5 hours."
                            cta="LOAD"
                            onClick={() => navigate('/buy-credits')}
                        />
                        <PromoCard
                            color="#00BFFF"
                            icon={<TrendingUp className="w-5 h-5" />}
                            title="Refer a Friend"
                            subtitle="Earn credits each time a friend signs up & loads."
                            cta="GET LINK"
                            onClick={() => navigate('/referrals')}
                        />
                    </div>
                </details>
            </div>
            <LobbyMusic />
            {/* Community Pot funding ticker removed per owner — Jun 2026 */}
        </div>
    );
};

// ---------- Sub-components ----------

const SectionHeader = ({ icon, title, rightAction, rightLabel }) => (
    <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-3">
            {icon}
            <h2 className="a11y-section-header">{title}</h2>
        </div>
        {rightAction && (
            <button onClick={rightAction}
                className="text-sm text-[#A0AAB2] hover:text-[#FFD700] font-semibold a11y-tap px-3"
                data-testid={`section-link-${title.toLowerCase().replace(/\s/g,'-')}`}>
                {rightLabel}
            </button>
        )}
    </div>
);

const WelcomeBanner = ({ name, balance, dailyBonusReady, onClaimBonus, pendingRollover = 0, pendingRolloverTotal = 0 }) => {  // eslint-disable-line no-unused-vars -- superseded by WalletStack (Jun 2026), kept for quick restore
    const { t } = useTranslation();
    // Flash credits when balance changes
    const prevBalance = useRef(balance);
    const [flash, setFlash] = useState(null); // 'up' | 'down' | null
    useEffect(() => {
        if (prevBalance.current === balance) return;
        const dir = balance > prevBalance.current ? 'up' : 'down';
        setFlash(dir);
        prevBalance.current = balance;
        const t = setTimeout(() => setFlash(null), 1400);
        return () => clearTimeout(t);
    }, [balance]);

    return (
        <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-2xl p-6 sm:p-8 relative overflow-hidden"
            style={{
                background: 'linear-gradient(135deg, rgba(255,215,0,0.08) 0%, rgba(0,0,0,0.6) 100%)',
                border: '1px solid rgba(255,215,0,0.45)',
            }}
            data-testid="lobby-welcome-banner"
        >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 relative z-10">
                <div>
                    <p className="a11y-section-eyebrow">{t('common.welcome_back')}</p>
                    <h1 className="a11y-heading text-4xl sm:text-5xl text-white">
                        {name}
                    </h1>
                </div>
                <div className="flex items-end gap-3">
                    <div className="text-left sm:text-right relative">
                        <p className="a11y-section-eyebrow">{t('common.credits')}</p>
                        <motion.p
                            key={`${balance}-${flash || 'idle'}`}
                            animate={
                                flash === 'up'
                                    ? { scale: [1, 1.08, 1] }
                                    : flash === 'down'
                                        ? { scale: [1, 0.96, 1] }
                                        : {}
                            }
                            transition={{ duration: 0.6, ease: 'easeOut' }}
                            className="a11y-bignum text-4xl sm:text-5xl text-[#39FF14]"
                            data-testid="lobby-balance"
                        >
                            {fmt$(balance)}
                        </motion.p>
                        {/* Floating "+win" indicator on increase */}
                        {flash === 'up' && (
                            <motion.span
                                initial={{ opacity: 0, y: 0 }}
                                animate={{ opacity: [0, 1, 0], y: -28 }}
                                transition={{ duration: 1.4, ease: 'easeOut' }}
                                className="absolute -top-1 right-0 text-xs font-black text-[#39FF14] pointer-events-none"
                                style={{ textShadow: '0 0 10px rgba(57,255,20,0.8)' }}
                            >
                                ▲ WIN
                            </motion.span>
                        )}
                    </div>
                    {dailyBonusReady && (
                        <button
                            onClick={onClaimBonus}
                            data-testid="lobby-claim-daily-bonus"
                            className="h-10 px-3 rounded-xl text-[11px] font-black flex items-center gap-1.5 animate-pulse"
                            style={{
                                background: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
                                color: '#000',
                                boxShadow: '0 0 16px rgba(255,215,0,0.4)',
                            }}
                        >
                            <Gift className="w-3.5 h-3.5" />
                            CLAIM DAILY
                        </button>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

const GameTile = ({ label, tagline, stat, cta, color, icon, bg, bgArt, onClick, disabled, testid, pill }) => {
    return (
        <motion.button
            onClick={disabled ? undefined : onClick}
            whileHover={disabled ? {} : { y: -4 }}
            whileTap={disabled ? {} : { scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 22 }}
            disabled={disabled}
            data-testid={testid}
            className={`tile-anim relative rounded-2xl p-6 sm:p-7 text-left overflow-hidden min-h-[180px] ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
            style={{
                background: bg,
                border: `2px solid ${color}66`,
                boxShadow: `0 0 24px ${color}22, inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -20px 40px rgba(0,0,0,0.35)`,
            }}
        >
            {/* Themed background art (poker chips, lotto balls, slot reels, sports balls) */}
            {bgArt && (
                <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
                    {bgArt}
                </div>
            )}
            {/* Attention-grabbing corner ribbon — used to clarify which tile leads to slots */}
            {pill && (
                <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: [1, 1.08, 1], opacity: 1 }}
                    transition={{ scale: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' }, opacity: { duration: 0.4 } }}
                    className="absolute top-3 right-3 z-20 pointer-events-none"
                >
                    <span
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wide shadow-lg"
                        style={{
                            background: color,
                            color: color === '#FF007F' || color === '#FF3B30' ? '#fff' : '#000',
                            boxShadow: `0 0 12px ${color}66`,
                        }}
                        data-testid={`${testid}-pill`}
                    >
                        {pill}
                    </span>
                </motion.div>
            )}
            <div className="relative z-10 flex flex-col h-full">
                <div className="flex items-start justify-between mb-3">
                    <div style={{ color }}>{icon}</div>
                    {!disabled && !pill && <ArrowRight className="w-5 h-5 text-white/50" />}
                </div>
                <h3 className="a11y-heading text-2xl text-white">{label}</h3>
                <p className="text-sm text-[#A0AAB2] mt-1 mb-4 flex-1">{tagline}</p>
                <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-bold" style={{ color }}>{stat}</span>
                    <span
                        className="a11y-chip"
                        style={{
                            background: color,
                            color: color === '#FF007F' || color === '#FF3B30' ? '#fff' : '#000',
                            border: 'none',
                        }}
                    >
                        {cta}
                    </span>
                </div>
            </div>
        </motion.button>
    );
};

const LotteryStateCard = ({ state, drawsByKey, cutoffs, onClick }) => {
    const findDraw = (window, pick) => drawsByKey[`${state}_${window}_${pick}`];
    const middayP3 = findDraw('midday', 3);
    const eveningP3 = findDraw('evening', 3);
    const middayCutoff = parseCutoff(cutoffs[`${state}_midday`]);
    const eveningCutoff = parseCutoff(cutoffs[`${state}_evening`]);
    const middayMins = middayCutoff ? minutesUntil(middayCutoff) : null;
    const eveningMins = eveningCutoff ? minutesUntil(eveningCutoff) : null;
    const color = STATE_COLORS[state];

    const renderRow = (label, draw, mins) => (
        <div className="flex items-center justify-between gap-2 py-1">
            <span className="text-[10px] text-[#A0AAB2] uppercase font-bold">{label}</span>
            {draw ? (
                <div className="flex gap-1">
                    {draw.winning_numbers.map((n, i) => (
                        <span key={i}
                            className="w-5 h-5 rounded text-[9px] font-black flex items-center justify-center"
                            style={{ background: `${color}25`, color, border: `1px solid ${color}50` }}>
                            {n}
                        </span>
                    ))}
                </div>
            ) : mins != null && mins > 0 ? (
                <span className="text-[9px] text-[#FFD700] font-bold flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />
                    {formatCountdown(mins)}
                </span>
            ) : (
                <span className="text-[9px] text-[#666] font-bold">awaiting</span>
            )}
        </div>
    );

    return (
        <button
            onClick={onClick}
            className="rounded-xl p-3 text-left hover:scale-[1.02] transition-transform"
            style={{ background: '#101010', border: `1px solid ${color}30` }}
            data-testid={`lobby-state-${state}`}
        >
            <div className="flex items-center justify-between mb-1">
                <span className="text-[12px] font-black" style={{ color }}>{state}</span>
                <span className="text-[8px] text-[#666] uppercase">{STATE_NAMES[state]}</span>
            </div>
            {renderRow('Midday', middayP3, middayMins)}
            <div className="border-t border-white/5 my-0.5" />
            {renderRow('Evening', eveningP3, eveningMins)}
        </button>
    );
};

const LiveGameCard = ({ game, onClick }) => {
    const start = (() => {
        try { return new Date(game.commence_time); } catch { return null; }
    })();
    const startStr = start
        ? start.toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' })
        : '';
    return (
        <button
            onClick={onClick}
            className="rounded-xl p-3 text-left bg-[#101010] hover:bg-[#161616] border border-white/5 hover:border-[#39FF14]/30 transition-all"
            data-testid={`lobby-live-game-${game.id}`}
        >
            <div className="flex items-center justify-between text-[9px] uppercase mb-1">
                <span className="text-[#39FF14] font-black tracking-wider">{game.sport_title || 'Sports'}</span>
                <span className="text-[#A0AAB2]">{startStr}</span>
            </div>
            <p className="text-[11px] font-bold text-white truncate">{game.home_team}</p>
            <p className="text-[10px] text-[#A0AAB2] truncate">vs {game.away_team}</p>
        </button>
    );
};

const PromoCard = ({ color, icon, title, subtitle, cta, onClick }) => (
    <button
        onClick={onClick}
        className="rounded-2xl p-5 text-left flex items-center gap-4 hover:-translate-y-0.5 transition-transform min-h-[88px] w-full"
        style={{ background: '#121212', border: `1px solid ${color}40` }}
        data-testid={`lobby-promo-${title.replace(/\s/g, '-').toLowerCase()}`}
    >
        <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}1A`, color }}>
            {icon}
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-base font-bold text-white">{title}</p>
            <p className="text-sm text-[#A0AAB2] truncate mt-0.5">{subtitle}</p>
        </div>
        <span
            className="a11y-chip"
            style={{
                background: color,
                color: color === '#FF007F' || color === '#FF3B30' ? '#fff' : '#000',
            }}
        >
            {cta}
        </span>
    </button>
);

export default Lobby;
